import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { InfoTooltip } from '../../../HOC/Tooltips';
import { DynamicBarChart } from 'src/App/Components/Charts';
import { getDummyMarketURL, getHeadingStyle, getSupplyByPropertyRevenue } from 'src/utils/functions';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_1_600, COLOR_PRIMARY_2, COLOR_TEXT_2 } from 'src/colors';
import {
  COLOR_E,
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  DOWNLOAD_CSV_KEY_METRICS,
  EXPERT_CTA,
  UNLOCK_CTA,
  exchangeRates,
} from 'src/const';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { VisibleListingsProp } from 'src/interfaces/iproptypes';
import { LoadingCard } from 'src/App/HOC';
import DownloadIcon from '@mui/icons-material/Download';
import NoDataCard from 'src/App/HOC/Cards/NoDataCard';
import { User } from 'src/interfaces/iuser';

const RevenuePotentialByPropertySizeCard = ({
  listings,
  handleOnSampleClick,
  isDisabled,
  currency,
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  handleDownloadCSV,
  noDataInMarket,
  setShowDemoBookingForm,
  dummyMarket
}: {
  listings: VisibleListingsProp;
  handleOnSampleClick: any;
  isDisabled: boolean;
  currency: string;
  cachedData: { [key: string]: number } | null | undefined;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' |'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  handleDownloadCSV: any;
  noDataInMarket: boolean;
  setShowDemoBookingForm: any;
  dummyMarket:any;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const currentPath = window.location.pathname;
  let dummyMarketURL = getDummyMarketURL(dummyMarket)
  const isDemoMarket = currentPath
    .toLowerCase()
    .includes(dummyMarketURL);
  const isAccessDenied = (isDisabled || user.subscriptionPlan != 'expert') && !isDemoMarket;
  const supplyForDummmyPropertyRevenue = val => {
    return [
      {
        keyShort: 'Studio',
        keyLong: 'Studio',
        value: 0.25 * val,
      },
      {
        keyShort: '1',
        keyLong: '1 bedroom',
        value: 0.48 * val,
      },
      {
        keyShort: '2',
        keyLong: '2 bedroom',
        value: 0.73 * val,
      },
      {
        keyShort: '3',
        keyLong: '3 bedroom',
        value: 0.56 * val,
      },
      {
        keyShort: '4',
        keyLong: '4 bedroom',
        value: 0.69 * val,
      },
      {
        keyShort: '5',
        keyLong: '5 bedroom',
        value: 0.4 * val,
      },
      {
        keyShort: '6+',
        keyLong: '6+ bedroom',
        value: 0.6 * val,
      },
      {
        keyShort: 'rooms',
        keyLong: 'private/hotel/shared rooms',
        value: 0.4 * val,
      },
    ];
  };

  const annualRevenue = useSelector(
    state => (state as any).dashboard.maxSampleRevPotentialPropertySize
  );

  const ExportButtonOptionTooltip = ({ body, children }) => {
    return (
      <InfoTooltip
        title={
          <Card>
            <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
              <Grid container direction="column" alignItems="flex-start">
                {body}
              </Grid>
            </CardContent>
          </Card>
        }
      >
        {children}
      </InfoTooltip>
    );
  };

  const isDataStatic = viewMode === 'dashboard' && cachedData;
  const isDataLoading =
    viewMode === 'dashboard'
      ? cachedData || isAccessDenied
        ? false
        : !listings.data?.length
      : multipleMarkersCallLoading && !listings.data?.length;

  const supplyByPropertyRevenue = useMemo(() => {
    return isAccessDenied
      ? supplyForDummmyPropertyRevenue(100)
      : isDataLoading
      ? []
      : getSupplyByPropertyRevenue(listings.data, currency, isDataStatic ? cachedData : null);
  }, [listings, currency, cachedData, viewMode]);

  if (noDataInMarket) {
    return <NoDataCard height={150} borderRadius={8} />;
  }

  if (isDataLoading || isDynamicLoading) {
    return <LoadingCard height={150} type="bars" />;
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      spacing={1}
      sx={{ padding: '5px', boxShadow: '0 4px 10px #0003', borderRadius: 1 }}
    >
      <Grid container item id="propertySizeGraph">
        <Grid item xs={12}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
          >
            <Typography variant="body1" className="subHeadings">
              By Property Size
              <InfoTooltip
                title={
                  <Card>
                    <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                      <Grid container direction="column" alignItems="flex-start">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          The average annual revenue potential for each different property size in
                          this market.
                        </Typography>
                      </Grid>
                    </CardContent>
                  </Card>
                }
                enterTouchDelay={0}
                leaveTouchDelay={10000}
              >
                <IconButton style={infoIconButton}>
                  <InfoOutlinedIcon style={infoIconStyle} />
                </IconButton>
              </InfoTooltip>
            </Typography>
            {/* <Box sx={{ cursor: 'pointer' }}>
              <ExportButtonOptionTooltip
                body={
                  <Typography variant="body2" style={{ fontWeight: 400 }}>
                    Download the Market Overview CSV to compare overall market statistics like
                    occupancy rate, ADR, revenue, between different property sizes.
                    <br />
                    <br />
                    <span style={{ fontWeight: 'bold' }}>
                      If the market is locked, you will get a sample CSV.
                    </span>
                  </Typography>
                }
              >
                <Box
                  onClick={() => {
                    // handleDownloadCSV(DOWNLOAD_CSV_KEY_METRICS);
                    setShowDemoBookingForm(true);
                  }}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {multipleMarkersCallLoading ? (
                    <CircularProgress size={20} sx={{ color: COLOR_PRIMARY_DARK }} />
                  ) : (
                    <DownloadIcon style={{ color: COLOR_PRIMARY_DARK }} />
                  )}
                </Box>
              </ExportButtonOptionTooltip>
            </Box> */}

            <Typography
              variant="body1"
              sx={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: COLOR_PRIMARY_2,
              }}
              onClick={() => {
                setShowDemoBookingForm(true);
              }}
            >
              <DownloadIcon sx={{ marginRight: '2px' }} /> Export
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <div style={{ position: 'relative' }}>
            <div
              style={{
                pointerEvents: isAccessDenied ? 'none' : 'auto',
              }}
            >
              <DynamicBarChart
                layout="horizontal"
                data={supplyByPropertyRevenue}
                label="Bedrooms"
                height={180}
                unit={exchangeRates[currency].symbol}
                barColor={COLOR_PRIMARY_1_600}
                barAccentColor={COLOR_PRIMARY_1}
                useShortHand={true}
                isCurrency={true}
              />
            </div>
            {isAccessDenied && (
              <div
                style={{
                  height: '81%',
                  position: 'absolute',
                  top: '0',
                  left: '30px',
                  width: '93%',
                  background: '#ffffff',
                  filter: 'blur( 6px)',
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              ></div>
            )}
            {isAccessDenied && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  fontSize: '20px',
                  fontWeight: 500,
                  color: COLOR_PRIMARY_2,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  textDecoration: 'underline',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => {
                  handleOnSampleClick('expert');
                }}
              >
                {user.subscriptionPlan !== 'expert' ? EXPERT_CTA : UNLOCK_CTA}
              </Typography>
            )}
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(RevenuePotentialByPropertySizeCard);
